/**
 * Environment Detection and Configuration
 * 
 * Detecta automáticamente el ambiente (UAT vs Production) y ajusta
 * el comportamiento del grid para manejar diferencias entre ambientes.
 */

export const ENV_CONFIG = {
  // Detectar ambiente por hostname
  isProduction: () => {
    const hostname = window.location.hostname;
    return hostname.includes('racetechnologies.com') && 
           !hostname.includes('uat') && 
           !hostname.includes('test') &&
           !hostname.includes('dev');
  },
  
  isUAT: () => {
    const hostname = window.location.hostname;
    return hostname.includes('uat') || 
           hostname.includes('test') ||
           hostname.includes('azurewebsites.net');
  },
  
  isDevelopment: () => {
    return process.env.NODE_ENV === 'development' || 
           window.location.hostname === 'localhost';
  },
  
  // Configuración específica por ambiente
  getConfig: () => {
    const isProduction = ENV_CONFIG.isProduction();
    const isUAT = ENV_CONFIG.isUAT();
    const isDevelopment = ENV_CONFIG.isDevelopment();
    
    return {
      // Ambiente actual
      environment: isProduction ? 'production' : (isUAT ? 'uat' : 'development'),
      
      // Delays para template loading
      templateLoadDelay: isProduction ? 500 : (isUAT ? 300 : 100),
      templateRetryDelay: isProduction ? 1000 : (isUAT ? 500 : 200),
      
      // Delays para filtros
      filterApplyDelay: isProduction ? 150 : (isUAT ? 100 : 50),
      
      // Auto-save intervals
      autoSaveInterval: isProduction ? 120000 : (isUAT ? 60000 : 30000),
      
      // Logging
      enableVerboseLogging: isDevelopment || localStorage.getItem('enableFilterDebug') === 'true',
      enableProductionLogging: isProduction,
      
      // Data handling
      // En producción, ser más estricto con validación de ParentID
      strictParentIdValidation: isProduction,
      
      // Performance
      useRequestIdleCallback: isProduction, // Solo en producción para mejor performance
      batchDOMUpdates: isProduction, // Usar beginUpdate/endUpdate solo en producción
      
      // Cache
      clearCacheOnLoad: false, // Cambiar a true si hay problemas de caché en producción
      storageVersion: 'v1', // Incrementar si necesitas invalidar caché
      
      // Feature flags
      enableEnhancedDiagnostics: isProduction || isUAT,
    };
  },
  
  // Helper para logging condicional
  log: (...args) => {
    const config = ENV_CONFIG.getConfig();
    if (config.enableVerboseLogging) {
      console.log(`[${config.environment.toUpperCase()}]`, ...args);
    }
  },
  
  // Helper para warnings que siempre se muestran
  warn: (...args) => {
    const config = ENV_CONFIG.getConfig();
    console.warn(`[${config.environment.toUpperCase()}]`, ...args);
  },
  
  // Helper para errores que siempre se muestran
  error: (...args) => {
    const config = ENV_CONFIG.getConfig();
    console.error(`[${config.environment.toUpperCase()}]`, ...args);
  },
  
  // Diagnósticos específicos por ambiente
  runEnvironmentDiagnostics: () => {
    const config = ENV_CONFIG.getConfig();
    
    console.log('=== ENVIRONMENT DIAGNOSTICS ===');
    console.log('Environment:', config.environment);
    console.log('Hostname:', window.location.hostname);
    console.log('React Mode:', process.env.NODE_ENV);
    console.log('Configuration:', config);
    
    // Verificar características específicas del ambiente
    const diagnostics = {
      environment: config.environment,
      hostname: window.location.hostname,
      userAgent: navigator.userAgent,
      screenSize: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      localStorage: {
        available: typeof localStorage !== 'undefined',
        sizeKB: typeof localStorage !== 'undefined' ? 
          (JSON.stringify(localStorage).length / 1024).toFixed(2) : 'N/A'
      },
      performance: {
        memoryAvailable: !!performance.memory,
        memoryUsedMB: performance.memory ? 
          (performance.memory.usedJSHeapSize / 1024 / 1024).toFixed(2) : 'N/A'
      },
      features: {
        requestIdleCallback: typeof requestIdleCallback !== 'undefined',
        requestAnimationFrame: typeof requestAnimationFrame !== 'undefined',
        fetch: typeof fetch !== 'undefined'
      }
    };
    
    console.table(diagnostics);
    return diagnostics;
  },
  
  // Función para comparar ambientes
  compareEnvironments: (uatData, prodData) => {
    console.log('=== ENVIRONMENT COMPARISON ===');
    
    const differences = {
      dataVolume: {
        uat: uatData?.totalRows || 'N/A',
        prod: prodData?.totalRows || 'N/A',
        difference: prodData?.totalRows - uatData?.totalRows || 'N/A'
      },
      parentIdTypes: {
        uat: uatData?.parentIdDistribution || {},
        prod: prodData?.parentIdDistribution || {}
      },
      performance: {
        uat: uatData?.performance || {},
        prod: prodData?.performance || {}
      }
    };
    
    console.table(differences);
    return differences;
  }
};

// Auto-run diagnostics si está habilitado
if (typeof window !== 'undefined' && localStorage.getItem('runEnvDiagnostics') === 'true') {
  ENV_CONFIG.runEnvironmentDiagnostics();
}

export default ENV_CONFIG;
